/**
 * New job form (shadcn, RM-117). RM-116: a job-kind selector chooses the job
 * type. Blender Render builds a single
 * `job_spec: { type: 'blender_render', mode_params: [{ mode: 'animation', ... }] }`
 * and runs the scene upload pipeline; Empty builds
 * `job_spec: { type: 'empty', wait_secs }` and needs no scene. On submit:
 * client-side validate (mirror types.rs validate_form) → submitJob → (Blender
 * only) upload pipeline (presign → PUT → confirm) → open /jobs?preview=<id>.
 *
 * DOM is the parity contract (specs + helpers/rm-app.ts): testids new-job-heading
 * / job-kind-select / engine-select / format-select (Base UI Select triggers) /
 * wait-secs-input (Empty) / scene-file-button / scene-file-input /
 * selected-file-name / selected-file-size / remove-file-button / scene-file-error
 * / submit-button; jobName placeholder "Auto z nazwy pliku"; number inputs in
 * order width, height, frame_start, frame_end, samples; billing stub text
 * "Szacowany koszt:"; validation errors carry class text-destructive.
 */
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useDomainLabels } from '../i18n/domain'
import { submitJob, submitTurntableFrameJob } from '../lib/endpoints/jobs'
import { qk } from '../lib/queryKeys'
import { TESTID } from '../lib/testids'
import { ApiError } from '../lib/errors'
import { UploadIndicator } from '../components/UploadIndicator'
import { useToast } from '../components/Toast'
import { useUpload } from '../hooks/useUpload'
import {
  Loader2,
  Send,
  ArrowLeft,
  Box,
  Sparkles,
  FlaskConical,
  Orbit,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  type JobKind,
  type RenderFormData,
  type UpscalerKind,
  type InvestmentUpscaler,
  type TargetMode,
  type TopazUpscaleFactor,
  type TopazPower,
  TOPAZ_UPSCALE_FACTORS,
  TOPAZ_POWERS,
  DEFAULT_FORM,
  validateForm,
  submitRequestFromForm,
  upscalingSpecFromForm,
  upscalingSpecEqual,
  applyPresetToForm,
  applyTargetMode,
  type InterpolationChoice,
} from './newJobForm'
import { useUpscalingPresets } from '../hooks/useUpscalingPresets'
import { useTopazVersions } from '../hooks/useTopazVersions'
import type { UpscalingPreset } from '../lib/schemas'

// ── Form state ──────────────────────────────────────────────────────────────
// Model + validation + submit-request building live in ./newJobForm (pure,
// unit-tested). This file owns rendering + the submit/upload effect.

function submitButtonLabel(
  isSubmitting: boolean,
  isUploading: boolean,
  t: TFunction,
): string {
  if (isUploading) return t('jobs.new.submit.uploading')
  if (isSubmitting) return t('jobs.new.submit.submitting')
  return t('jobs.new.submit.idle')
}

const ERROR_CLASS = 'mt-1 text-xs text-destructive'

/**
 * Map a server validation `detail` (untranslated) onto specific form fields, so
 * the offending input is highlighted instead of only showing a generic toast.
 * Recognises the RM-117 upscaling job_spec messages.
 */
function mapDetailToFields(
  detail: string | undefined,
  t: TFunction,
): Record<string, string> {
  if (!detail) return {}
  const out: Record<string, string> = {}
  if (detail.includes('view_id'))
    out.view_id = t('entity.upscaling.field.viewId.error.rejected')
  if (detail.includes('target_size'))
    out.target_size = t('entity.upscaling.field.targetSize.error.rejected')
  // Topaz factor/power are fixed-choice selects, so the server can't reject their
  // values — no field mapping needed beyond view_id / target_size.
  return out
}

/** Icon per job type; labels/hints come from the domain locale (enum.jobKind). */
const JOB_KINDS: { kind: JobKind; icon: LucideIcon }[] = [
  { kind: 'blender_render', icon: Box },
  { kind: 'upscaling', icon: Sparkles },
  { kind: 'turntable_frame_bake', icon: Orbit },
  { kind: 'empty', icon: FlaskConical },
]

/** Step 1 of the New Render flow: pick a type. Fills the body — nothing else. */
function JobKindTiles({ onPick }: { onPick: (kind: JobKind) => void }) {
  const d = useDomainLabels()
  return (
    <div className="flex flex-col gap-3">
      {JOB_KINDS.map(({ kind, icon: Icon }) => (
        <button
          key={kind}
          type="button"
          data-testid={TESTID.jobKindTile(kind)}
          onClick={() => onPick(kind)}
          className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-accent focus-visible:border-primary focus-visible:outline-none"
        >
          <Icon className="size-8 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="text-base font-semibold text-foreground">
              {d.jobKind(kind)}
            </div>
            <div className="text-sm text-muted-foreground">
              {d.jobKindHint(kind)}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

/**
 * A bordered panel that keeps a control and the fields it reveals in one box, so
 * a dependent field (e.g. the Topaz-only factor/power) visibly belongs to
 * the select that sits at the top of the same panel.
 */
function FieldGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      {children}
    </div>
  )
}

/**
 * Topaz factor (2×/4×/6×) + power (Low/Medium/High) selects — the split Topaz
 * controls, shared by the main-upscaler and building-overlay panels.
 */
function TopazControls({
  factor,
  power,
  onFactor,
  onPower,
}: {
  factor: TopazUpscaleFactor
  power: TopazPower
  onFactor: (f: TopazUpscaleFactor) => void
  onPower: (p: TopazPower) => void
}) {
  const d = useDomainLabels()
  const factorItems = Object.fromEntries(
    TOPAZ_UPSCALE_FACTORS.map((f) => [String(f), `${f}×`]),
  )
  const powerItems = Object.fromEntries(
    TOPAZ_POWERS.map((p) => [p, d.value('upscaling', 'topazPower', p)]),
  )
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label>{d.label('upscaling', 'topazUpscaleFactor')}</Label>
        <Select
          items={factorItems}
          value={String(factor)}
          onValueChange={(v) =>
            onFactor(Number(v ?? factor) as TopazUpscaleFactor)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOPAZ_UPSCALE_FACTORS.map((f) => (
              <SelectItem key={f} value={String(f)}>{`${f}×`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>{d.label('upscaling', 'topazPower')}</Label>
        <Select
          items={powerItems}
          value={power}
          onValueChange={(v) => onPower((v ?? power) as TopazPower)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOPAZ_POWERS.map((p) => (
              <SelectItem key={p} value={p}>
                {d.value('upscaling', 'topazPower', p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// chaiNNer resize interpolation options, grouped like the Resize To Side node.
// Values are the job_spec::InterpolationMethod tokens; '' = leave chain default.
const INTERP_BASIC: { value: InterpolationChoice; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'nearest', label: 'Nearest Neighbor' },
  { value: 'box', label: 'Area (Box)' },
  { value: 'linear', label: 'Linear' },
  { value: 'cubic', label: 'Cubic' },
  { value: 'lanczos', label: 'Lanczos' },
]
const INTERP_ADVANCED: { value: InterpolationChoice; label: string }[] = [
  { value: 'hermite', label: 'Hermite' },
  { value: 'mitchell', label: 'Mitchell' },
  { value: 'b_spline', label: 'B-Spline' },
  { value: 'hamming', label: 'Hamming' },
  { value: 'hann', label: 'Hann' },
  { value: 'lagrange', label: 'Lagrange' },
  { value: 'gauss', label: 'Gauss' },
]
const INTERP_DEFAULT = '__default__' // sentinel for '' (Base UI Select needs a non-empty value)

/**
 * One interpolation-method select for a single resize role. Shows a "chain
 * default" option plus the Basic/Advanced groups mirroring chaiNNer's node.
 */
function InterpolationSelect({
  label,
  value,
  onChange,
}: {
  label: string
  value: InterpolationChoice
  onChange: (v: InterpolationChoice) => void
}) {
  const { t } = useTranslation()
  const items: Record<string, string> = {
    [INTERP_DEFAULT]: t('entity.upscaling.field.interpolation.default'),
    ...Object.fromEntries(
      [...INTERP_BASIC, ...INTERP_ADVANCED].map((o) => [o.value, o.label]),
    ),
  }
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select
        items={items}
        value={value === '' ? INTERP_DEFAULT : value}
        onValueChange={(v) =>
          onChange(
            (v === INTERP_DEFAULT || v == null ? '' : v) as InterpolationChoice,
          )
        }
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INTERP_DEFAULT}>
            {t('entity.upscaling.field.interpolation.default')}
          </SelectItem>
          <SelectGroup>
            <SelectLabel>
              {t('entity.upscaling.field.interpolation.basic')}
            </SelectLabel>
            {INTERP_BASIC.map((o) => (
              <SelectItem key={o.value} value={o.value as string}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>
              {t('entity.upscaling.field.interpolation.advanced')}
            </SelectLabel>
            {INTERP_ADVANCED.map((o) => (
              <SelectItem key={o.value} value={o.value as string}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * Two-step New Render: pick a type (tiles), then its parameter form. Used by the
 * New-render sheet and the `/jobs/new` page.
 */
export function NewJobFlow() {
  const [kind, setKind] = useState<JobKind | undefined>(undefined)
  if (kind === undefined) return <JobKindTiles onPick={setKind} />
  return <NewJobForm kind={kind} onChangeType={() => setKind(undefined)} />
}

/**
 * Global, shared upscaling presets. Pick one to apply its config to the form.
 *
 * The action next to the select depends on state:
 *   - a preset is selected and the form still matches it → no button (select only)
 *   - the form drifted from the selected preset the user can edit → "Override"
 *   - "custom" (no preset), or a drifted preset the user can't edit → "Save preset"
 *     (opens a name dialog; save-as-new)
 * Each editable preset in the dropdown carries a trash icon to delete it.
 * The per-view `view_id` is not part of the preset, so editing it never marks the
 * preset dirty.
 */
function UpscalingPresetPicker({
  form,
  setForm,
}: {
  form: RenderFormData
  setForm: Dispatch<SetStateAction<RenderFormData>>
}) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const { query, create, update, remove } = useUpscalingPresets()
  const [selectedId, setSelectedId] = useState('')
  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  // A creator may publish their own preset immediately or keep it private.
  const [savePublic, setSavePublic] = useState(false)

  const CUSTOM = '__custom__'
  const presets = query.data?.presets ?? []
  const selected = presets.find((p) => p.id === selectedId) ?? null
  const dirty = selected
    ? !upscalingSpecEqual(upscalingSpecFromForm(form), selected.spec)
    : false

  // Tag the job with the preset name only while a clean preset is selected; a
  // custom job or one edited away from its preset carries no name (the worker
  // then tags the render `custom enhancement`). preset_name is metadata only —
  // it isn't part of the spec compared by `dirty`, so writing it can't loop.
  const effectivePresetName = selected && !dirty ? selected.name : undefined
  useEffect(() => {
    setForm((f) =>
      f.presetName === effectivePresetName
        ? f
        : { ...f, presetName: effectivePresetName },
    )
  }, [effectivePresetName, setForm])

  const onError = (e: Error) =>
    showToast({ title: e.message, variant: 'error' })

  const onSelect = (value: string) => {
    if (value === CUSTOM) {
      setSelectedId('')
      return
    }
    setSelectedId(value)
    const p = presets.find((x) => x.id === value)
    if (p) setForm((f) => ({ ...f, ...applyPresetToForm(p.spec) }))
  }

  const onSaveNew = () => {
    const name = saveName.trim()
    if (name === '') return
    create.mutate(
      {
        name,
        spec: upscalingSpecFromForm(form),
        isPublic: savePublic,
      },
      {
        onSuccess: (preset) => {
          setSelectedId(preset.id)
          setSaveName('')
          setSavePublic(false)
          setNameDialogOpen(false)
          showToast({
            title: t('preset.saved', { name: preset.name }),
            variant: 'info',
          })
        },
        onError,
      },
    )
  }

  const onOverride = () => {
    if (!selected) return
    update.mutate(
      {
        id: selected.id,
        name: selected.name,
        spec: upscalingSpecFromForm(form),
        // Preserve the current visibility — overriding params must never flip it.
        isPublic: selected.is_public,
      },
      {
        onSuccess: () =>
          showToast({
            title: t('preset.updated', { name: selected.name }),
            variant: 'info',
          }),
        onError,
      },
    )
  }

  // A creator (or system admin) may flip an editable preset's visibility.
  const onTogglePublic = (p: UpscalingPreset) => {
    update.mutate(
      { id: p.id, name: p.name, spec: p.spec, isPublic: !p.is_public },
      {
        onSuccess: (updated) =>
          showToast({
            title: t(
              updated.is_public ? 'preset.published' : 'preset.unpublished',
              { name: updated.name },
            ),
            variant: 'info',
          }),
        onError,
      },
    )
  }

  const onDelete = (p: UpscalingPreset) => {
    remove.mutate(p.id, {
      onSuccess: () => {
        showToast({
          title: t('preset.deleted', { name: p.name }),
          variant: 'info',
        })
        if (p.id === selectedId) setSelectedId('')
      },
      onError,
    })
  }

  const items: Record<string, string> = {
    [CUSTOM]: t('preset.custom'),
    ...Object.fromEntries(presets.map((p) => [p.id, p.name])),
  }

  // Override an editable preset the form drifted from; otherwise (custom, or a
  // drifted preset the user can't override) offer save-as-new. A clean selected
  // preset shows no button at all.
  const canOverride = selected != null && dirty && selected.can_edit
  const canSaveNew = selected == null || (dirty && !selected.can_edit)

  return (
    <>
      <div className="space-y-1.5">
        <Label>{t('preset.label')}</Label>
        <div className="flex items-center gap-2">
          <Select
            items={items}
            value={selected ? selectedId : CUSTOM}
            onValueChange={(v) => onSelect((v as string | null) ?? CUSTOM)}
          >
            <SelectTrigger className="min-w-0 flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CUSTOM}>{t('preset.custom')}</SelectItem>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.can_edit ? (
                    // Creator/admin: click the pill to publish/unpublish.
                    // State is shown by BACKGROUND colour — the SelectItem hover
                    // highlight recolours text only, so it can't wash the pill out.
                    <button
                      type="button"
                      aria-label={t('preset.togglePublicAria', {
                        name: p.name,
                      })}
                      title={t('preset.publicTitle')}
                      className={`ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase transition-colors ${
                        p.is_public
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-primary/20'
                      }`}
                      disabled={update.isPending}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onTogglePublic(p)
                      }}
                    >
                      {t(p.is_public ? 'preset.public' : 'preset.private')}
                    </button>
                  ) : (
                    p.is_public && (
                      <span className="ml-2 shrink-0 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-primary-foreground uppercase">
                        {t('preset.public')}
                      </span>
                    )
                  )}
                  {p.can_edit && (
                    <button
                      type="button"
                      aria-label={t('preset.deleteAria', { name: p.name })}
                      className="ml-1 rounded p-0.5 text-muted-foreground hover:text-destructive"
                      disabled={remove.isPending}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onDelete(p)
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {canOverride && (
            <Button
              type="button"
              variant="outline"
              onClick={onOverride}
              disabled={update.isPending}
            >
              {t('preset.override')}
            </Button>
          )}
          {canSaveNew && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSaveName('')
                setSavePublic(false)
                setNameDialogOpen(true)
              }}
            >
              {t('preset.save')}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('preset.saveDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('preset.saveDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t('preset.nameLabel')}</Label>
            <Input
              type="text"
              autoFocus
              placeholder={t('preset.namePlaceholder')}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSaveNew()
                }
              }}
            />
          </div>
          <label className="flex items-start gap-2.5">
            <Checkbox
              checked={savePublic}
              onCheckedChange={(checked) => setSavePublic(checked === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-muted-foreground">
              {t('preset.makePublic')}
            </span>
          </label>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNameDialogOpen(false)}
            >
              {t('common.action.cancel')}
            </Button>
            <Button
              type="button"
              onClick={onSaveNew}
              disabled={saveName.trim() === '' || create.isPending}
            >
              {t('common.action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * The New Render parameter form (card body + submit/upload logic) for a chosen
 * job `kind`. On success it navigates to `/jobs?preview=<id>`, which also
 * dismisses the sheet (the `?new` param is dropped). `onChangeType` returns to
 * the type picker.
 */
export function NewJobForm({
  kind,
  onChangeType,
}: {
  kind: JobKind
  onChangeType: () => void
}) {
  const [form, setForm] = useState<RenderFormData>({
    ...DEFAULT_FORM,
    jobKind: kind,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const rawFile = useRef<File | null>(null)
  const { t } = useTranslation()
  const d = useDomainLabels()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { showToast } = useToast()
  const { state: uploadState, upload, reset: resetUpload } = useUpload()
  const topazVersionsQuery = useTopazVersions()
  const topazVersions = Array.from(
    new Set([
      ...(topazVersionsQuery.data?.versions ?? []),
      ...(form.topazVersion ? [form.topazVersion] : []),
    ]),
  ).sort()

  const isUploading =
    uploadState.kind === 'uploading' || uploadState.kind === 'reading'

  const submitMutation = useMutation({
    mutationFn: async (formData: RenderFormData) => {
      // Step 1: Submit job (no scene_key yet — we upload after).
      const req = submitRequestFromForm(formData)
      const resp =
        formData.jobKind === 'turntable_frame_bake'
          ? await submitTurntableFrameJob(req)
          : await submitJob(req)
      const jobId = resp.data.id

      // Step 2: Upload scene file to R2 (if we have a file).
      const file = rawFile.current
      if (file) {
        try {
          await upload(jobId, file)
        } catch (error) {
          showToast({
            title: t('entity.blenderRender.field.scene.error.uploadFailed', {
              message: error instanceof Error ? error.message : String(error),
            }),
            variant: 'error',
          })
          // Still open the job preview — job exists, just no scene yet.
          await nav({ to: '/jobs', search: { preview: jobId }, replace: true })
          return
        }
      }

      // Step 3: Open the new job's preview on the list.
      await qc.invalidateQueries({ queryKey: qk.jobs() })
      await nav({ to: '/jobs', search: { preview: jobId } })
    },
    onError: (error) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : String(error)
      // Untranslated server reason (field name, deserialize rejection) — the
      // most actionable text we get. Show it in a persistent banner and map it
      // onto the offending field when we can recognise it.
      const detail = error instanceof ApiError ? error.detail : undefined
      setSubmitError(
        detail && detail !== message ? `${message} — ${detail}` : message,
      )
      const fieldErrors = mapDetailToFields(detail, t)
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }))
      }
      showToast({ title: message, variant: 'error' })
    },
  })

  const isSubmitting = submitMutation.isPending

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    rawFile.current = file
    setForm((f) => ({
      ...f,
      sceneFile: { name: file.name, size: file.size },
    }))
    setErrors((prev) => {
      const rest = { ...prev }
      delete rest.scene_file
      return rest
    })
  }

  const onRemoveFile = () => {
    rawFile.current = null
    setForm((f) => ({ ...f, sceneFile: null }))
    resetUpload()
  }

  const onManifestSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const manifest = JSON.parse(await file.text()) as unknown
      setForm((current) => ({
        ...current,
        manifest,
        manifestFile: { name: file.name, size: file.size },
      }))
      setErrors((current) => {
        const next = { ...current }
        delete next.manifest
        return next
      })
    } catch {
      setForm((current) => ({ ...current, manifest: null, manifestFile: null }))
      setErrors((current) => ({ ...current, manifest: 'Invalid JSON file' }))
    }
  }

  const onSubmit = () => {
    setSubmitError(null)
    const validationErrors = validateForm(form, t)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    submitMutation.mutate(form)
  }

  const sizeMb =
    form.sceneFile !== null ? form.sceneFile.size / (1024 * 1024) : 0
  const submitLabel = submitButtonLabel(isSubmitting, isUploading, t)
  const isBlender = form.jobKind === 'blender_render'
  const isEmpty = form.jobKind === 'empty'
  const isUpscaling = form.jobKind === 'upscaling'
  const isTurntable = form.jobKind === 'turntable_frame_bake'

  const kindLabel = d.jobKind(kind)

  return (
    <div className="flex flex-col gap-6">
      {/* Chosen type + return to the tile picker */}
      <button
        type="button"
        onClick={onChangeType}
        data-testid={TESTID.jobKindBack}
        className="flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        <span className="font-medium text-foreground">{kindLabel}</span>
        <span>· {t('jobs.new.changeType')}</span>
      </button>

      {/* Empty job: wait-seconds field */}
      {isEmpty && (
        <div className="space-y-1.5">
          <Label>{d.label('empty', 'waitSecs')}</Label>
          <Input
            type="number"
            data-testid={TESTID.waitSecsInput}
            value={form.waitSecs}
            onChange={(e) =>
              setForm((f) => ({ ...f, waitSecs: e.target.value }))
            }
          />
          {errors.wait_secs !== undefined && (
            <p className={ERROR_CLASS} data-testid={TESTID.waitSecsError}>
              {errors.wait_secs}
            </p>
          )}
          <Label className="pt-2">{d.label('empty', 'exitCode')}</Label>
          <Input
            type="number"
            data-testid={TESTID.exitCodeInput}
            value={form.exitCode}
            onChange={(e) =>
              setForm((f) => ({ ...f, exitCode: e.target.value }))
            }
          />
          {errors.exit_code !== undefined && (
            <p className={ERROR_CLASS} data-testid={TESTID.exitCodeError}>
              {errors.exit_code}
            </p>
          )}
        </div>
      )}

      {/* Upscaling job (RM-117) */}
      {isUpscaling && (
        <>
          {/* Per-view identifiers — the view being upscaled and its optional
                building id. These are NOT part of a preset (a preset is reusable
                across views), so they live in their own section at the top. */}
          <FieldGroup>
            <div className="space-y-1.5">
              <Label>{d.label('upscaling', 'viewId')}</Label>
              <Input
                type="text"
                placeholder={d.placeholder('upscaling', 'viewId')}
                value={form.viewId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, viewId: e.target.value }))
                }
              />
              {errors.view_id !== undefined && (
                <p className={ERROR_CLASS}>{errors.view_id}</p>
              )}
            </div>
          </FieldGroup>

          {/* Preset picker + everything it drives, bound into one section: the
                picker sits on top; the preset-dependent config sits below on a
                muted panel so it visibly belongs to (and is driven by) the
                selected preset. */}
          <section className="overflow-hidden rounded-lg border border-border">
            <div className="p-4">
              <UpscalingPresetPicker form={form} setForm={setForm} />
            </div>

            <div className="space-y-4 border-t border-border bg-muted/40 p-4">
              {/* Output size is part of the preset config but independent of
                    the upscaler/overlay choices, so it gets its own row. The mode
                    picks an explicit width vs. native (widest source → Topaz). */}
              <div className="space-y-1.5">
                <Label>{t('entity.upscaling.field.targetMode.label')}</Label>
                <Select
                  items={{
                    width: t(
                      'entity.upscaling.field.targetMode.value.width.label',
                    ),
                    widest: t(
                      'entity.upscaling.field.targetMode.value.widest.label',
                    ),
                  }}
                  value={form.targetMode}
                  onValueChange={(v) =>
                    setForm((f) =>
                      applyTargetMode(f, (v ?? f.targetMode) as TargetMode),
                    )
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="width">
                      {t('entity.upscaling.field.targetMode.value.width.label')}
                    </SelectItem>
                    <SelectItem value="widest">
                      {t(
                        'entity.upscaling.field.targetMode.value.widest.label',
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {form.targetMode === 'width' ? (
                  <>
                    <Input
                      type="number"
                      value={form.targetSize}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10)
                        if (!Number.isNaN(v))
                          setForm((f) => ({ ...f, targetSize: v }))
                      }}
                    />
                    {errors.target_size !== undefined && (
                      <p className={ERROR_CLASS}>{errors.target_size}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('entity.upscaling.field.targetMode.value.widest.hint')}
                  </p>
                )}
              </div>

              {/* Main upscaler + the Topaz-only factor/power it reveals,
                grouped in one box so the revealed field belongs to it. */}
              <FieldGroup>
                <div className="space-y-1.5">
                  <Label>{d.label('upscaling', 'upscaler')}</Label>
                  <Select
                    items={{
                      Topaz: d.upscaler('Topaz'),
                      Simple: d.upscaler('Simple'),
                      ClearReality: d.upscaler('ClearReality'),
                    }}
                    value={form.mainUpscaler}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        mainUpscaler: (v ?? f.mainUpscaler) as UpscalerKind,
                      }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Topaz">
                        {d.upscaler('Topaz')}
                      </SelectItem>
                      {/* Native mode re-details via Topaz only — Simple/ClearReality
                        would be a no-op at native width, so they're hidden. */}
                      {form.targetMode === 'width' && (
                        <>
                          <SelectItem value="Simple">
                            {d.upscaler('Simple')}
                          </SelectItem>
                          <SelectItem value="ClearReality">
                            {d.upscaler('ClearReality')}
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Interpolation lives in the same box as the upscaler it belongs
                  to (experimental). Simple → one resize; Topaz → factor/power +
                  a separate pre-Topaz downscale and final resize interpolation. */}
                {form.mainUpscaler === 'Simple' && (
                  <InterpolationSelect
                    label={t('entity.upscaling.field.interpolation.simple')}
                    value={form.mainSimpleInterpolation}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, mainSimpleInterpolation: v }))
                    }
                  />
                )}
                {form.mainUpscaler === 'Topaz' && (
                  <>
                    <TopazControls
                      factor={form.topazUpscaleFactor}
                      power={form.topazPower}
                      onFactor={(topazUpscaleFactor) =>
                        setForm((f) => ({ ...f, topazUpscaleFactor }))
                      }
                      onPower={(topazPower) =>
                        setForm((f) => ({ ...f, topazPower }))
                      }
                    />
                    <InterpolationSelect
                      label={t(
                        'entity.upscaling.field.interpolation.downscale',
                      )}
                      value={form.mainDownscaleInterpolation}
                      onChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          mainDownscaleInterpolation: v,
                        }))
                      }
                    />
                    <InterpolationSelect
                      label={t('entity.upscaling.field.interpolation.resize')}
                      value={form.mainResizeInterpolation}
                      onChange={(v) =>
                        setForm((f) => ({ ...f, mainResizeInterpolation: v }))
                      }
                    />
                  </>
                )}
              </FieldGroup>

              {(form.mainUpscaler === 'Topaz' ||
                form.investmentUpscaler === 'Topaz') && (
                <>
                  <FieldGroup>
                    <label className="flex items-start gap-2.5">
                      <Checkbox
                        data-testid="max-brightness-adjustment"
                        checked={form.maxBrightnessAdjustment}
                        onCheckedChange={(checked) =>
                          setForm((current) => ({
                            ...current,
                            maxBrightnessAdjustment: checked === true,
                          }))
                        }
                        className="mt-0.5"
                      />
                      <span className="space-y-1">
                        <span className="block text-sm font-medium">
                          {t(
                            'entity.upscaling.field.maxBrightnessAdjustment.label',
                          )}
                        </span>
                        <span className="block text-sm text-muted-foreground">
                          {t(
                            'entity.upscaling.field.maxBrightnessAdjustment.hint',
                          )}
                        </span>
                      </span>
                    </label>
                  </FieldGroup>
                  <FieldGroup>
                    <div className="space-y-1.5">
                      <Label>
                        {t('entity.upscaling.field.topazVersion.label')}
                      </Label>
                      <Select
                        items={{
                          __any__: t('entity.upscaling.field.topazVersion.any'),
                          ...Object.fromEntries(
                            topazVersions.map((version) => [version, version]),
                          ),
                        }}
                        value={form.topazVersion || '__any__'}
                        onValueChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            topazVersion:
                              value === '__any__' ? '' : (value ?? ''),
                          }))
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__any__">
                            {t('entity.upscaling.field.topazVersion.any')}
                          </SelectItem>
                          {topazVersions.map((version) => (
                            <SelectItem key={version} value={version}>
                              {version}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        {topazVersionsQuery.isError
                          ? t('entity.upscaling.field.topazVersion.unavailable')
                          : t('entity.upscaling.field.topazVersion.hint')}
                      </p>
                    </div>
                  </FieldGroup>
                </>
              )}

              {/* Building overlay + the params it reveals (Topaz scale, merge
                feather), grouped in one box below the overlay select. */}
              <FieldGroup>
                <div className="space-y-1.5">
                  <Label>{d.label('upscaling', 'investmentOverlay')}</Label>
                  <Select
                    items={{
                      none: d.investmentOverlay('none'),
                      Simple: d.investmentOverlay('Simple'),
                      Topaz: d.investmentOverlay('Topaz'),
                      ClearReality: d.investmentOverlay('ClearReality'),
                      Source: d.investmentOverlay('Source'),
                    }}
                    value={form.investmentUpscaler}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        investmentUpscaler: (v ??
                          f.investmentUpscaler) as InvestmentUpscaler,
                      }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {d.investmentOverlay('none')}
                      </SelectItem>
                      {/* Native mode: Simple/ClearReality are no-ops → hidden, and
                        the raw-source overlay ('Source') is offered instead. */}
                      {form.targetMode === 'width' && (
                        <SelectItem value="Simple">
                          {d.investmentOverlay('Simple')}
                        </SelectItem>
                      )}
                      <SelectItem value="Topaz">
                        {d.investmentOverlay('Topaz')}
                      </SelectItem>
                      {form.targetMode === 'width' && (
                        <SelectItem value="ClearReality">
                          {d.investmentOverlay('ClearReality')}
                        </SelectItem>
                      )}
                      {form.targetMode === 'widest' && (
                        <SelectItem value="Source">
                          {d.investmentOverlay('Source')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Overlay-only params — revealed inside the same box once an
                  overlay upscaler is chosen (merge feather has no effect
                  without an overlay to blend). Interpolation is scoped to the
                  investment branch, separate from the main image. */}
                {form.investmentUpscaler === 'Simple' && (
                  <InterpolationSelect
                    label={t('entity.upscaling.field.interpolation.simple')}
                    value={form.investmentSimpleInterpolation}
                    onChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        investmentSimpleInterpolation: v,
                      }))
                    }
                  />
                )}
                {form.investmentUpscaler === 'Topaz' && (
                  <>
                    <TopazControls
                      factor={form.investmentUpscaleFactor}
                      power={form.investmentPower}
                      onFactor={(investmentUpscaleFactor) =>
                        setForm((f) => ({ ...f, investmentUpscaleFactor }))
                      }
                      onPower={(investmentPower) =>
                        setForm((f) => ({ ...f, investmentPower }))
                      }
                    />
                    <InterpolationSelect
                      label={t(
                        'entity.upscaling.field.interpolation.downscale',
                      )}
                      value={form.investmentDownscaleInterpolation}
                      onChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          investmentDownscaleInterpolation: v,
                        }))
                      }
                    />
                    <InterpolationSelect
                      label={t('entity.upscaling.field.interpolation.resize')}
                      value={form.investmentResizeInterpolation}
                      onChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          investmentResizeInterpolation: v,
                        }))
                      }
                    />
                  </>
                )}
                {form.investmentUpscaler === 'Source' && (
                  <p className="text-sm text-muted-foreground">
                    {t('entity.upscaling.field.investmentOverlay.sourceHint')}
                  </p>
                )}
                {form.investmentUpscaler !== 'none' && (
                  <div className="space-y-1.5">
                    <Label>{d.label('upscaling', 'mergeFeather')}</Label>
                    <Input
                      type="number"
                      placeholder={d.placeholder('upscaling', 'mergeFeather')}
                      value={form.mergeFeatherPx}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          mergeFeatherPx: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}
                {form.investmentUpscaler !== 'none' && (
                  <div className="space-y-1.5">
                    <Label>{d.label('upscaling', 'frameElements')}</Label>
                    <Input
                      type="number"
                      min={1}
                      placeholder={d.placeholder('upscaling', 'frameElements')}
                      value={form.frameElementsId}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          frameElementsId: e.target.value,
                        }))
                      }
                    />
                    <p className="text-sm text-muted-foreground">
                      {d.hint('upscaling', 'frameElements')}
                    </p>
                  </div>
                )}
              </FieldGroup>
            </div>
          </section>
        </>
      )}

      {isTurntable && (
        <FieldGroup>
          <div className="space-y-1.5">
            <Label>Color Creator view ID</Label>
            <Input
              data-testid={TESTID.turntableColorViewId}
              value={form.colorViewId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  colorViewId: event.target.value,
                }))
              }
            />
            {errors.color_view_id && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableColorViewIdError}
              >
                {errors.color_view_id}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Depth Creator view ID</Label>
            <Input
              data-testid={TESTID.turntableDepthViewId}
              value={form.depthViewId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  depthViewId: event.target.value,
                }))
              }
            />
            {errors.depth_view_id && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableDepthViewIdError}
              >
                {errors.depth_view_id}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Output target ID</Label>
            <Input
              data-testid={TESTID.turntableOutputTargetId}
              value={form.outputTargetId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  outputTargetId: event.target.value,
                }))
              }
            />
            {errors.output_target_id && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableOutputTargetIdError}
              >
                {errors.output_target_id}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Delivery API key</Label>
            <Input
              type="password"
              autoComplete="off"
              data-testid={TESTID.turntableDeliveryApiKey}
              value={form.deliveryApiKey}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  deliveryApiKey: event.target.value,
                }))
              }
            />
            {errors.delivery_api_key && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableDeliveryApiKeyError}
              >
                {errors.delivery_api_key}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Output frames</Label>
            <Input
              type="number"
              min={1}
              max={2400}
              data-testid={TESTID.turntableOutputFrameCount}
              value={form.outputFrameCount}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  outputFrameCount: event.target.value,
                }))
              }
            />
            {errors.output_frame_count && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableOutputFrameCountError}
              >
                {errors.output_frame_count}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Input manifest JSON</Label>
            <Input
              type="file"
              accept="application/json,.json"
              data-testid={TESTID.turntableManifestFile}
              onChange={onManifestSelected}
            />
            {form.manifestFile && (
              <p className="text-sm text-muted-foreground">
                {form.manifestFile.name}
              </p>
            )}
            {errors.manifest && (
              <p
                className={ERROR_CLASS}
                data-testid={TESTID.turntableManifestFileError}
              >
                {errors.manifest}
              </p>
            )}
          </div>
        </FieldGroup>
      )}

      {/* File upload zone (Blender only) */}
      {isBlender && (
        <div>
          <Label className="mb-2">{d.label('blenderRender', 'scene')}</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-muted-foreground transition-colors">
            {form.sceneFile !== null ? (
              <div>
                <p
                  className="text-sm font-medium text-foreground"
                  data-testid={TESTID.selectedFileName}
                >
                  {form.sceneFile.name}
                </p>
                <p
                  className="text-xs text-muted-foreground"
                  data-testid={TESTID.selectedFileSize}
                >
                  {t('common.unit.mb', { size: sizeMb.toFixed(1) })}
                </p>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-2 h-auto p-0 text-destructive"
                  data-testid={TESTID.removeFileButton}
                  onClick={onRemoveFile}
                >
                  {t('common.action.remove')}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-muted-foreground">
                  {d.hint('blenderRender', 'scene')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('entity.blenderRender.field.scene.maxSize')}
                </p>
                <label
                  className="mt-3 inline-block cursor-pointer"
                  data-testid={TESTID.sceneFileButton}
                >
                  <input
                    type="file"
                    accept=".blend"
                    className="hidden"
                    data-testid={TESTID.sceneFileInput}
                    onChange={onFileSelected}
                  />
                  <span className="inline-flex items-center rounded-lg bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground hover:bg-secondary/80">
                    {t('common.action.chooseFile')}
                  </span>
                </label>
              </div>
            )}
          </div>
          {/* Upload progress indicator */}
          <UploadIndicator state={uploadState} />
          {errors.scene_file !== undefined && (
            <p className={ERROR_CLASS} data-testid={TESTID.sceneFileError}>
              {errors.scene_file}
            </p>
          )}
        </div>
      )}

      {/* Name (shared by all job types). Fileless job types default their
            blank name from their own fields — upscaling from the view id, empty
            from wait + exit code — so the placeholder reflects that per type. */}
      <div className="space-y-1.5">
        <Label>{d.label('job', 'name')}</Label>
        <Input
          type="text"
          placeholder={
            isUpscaling
              ? d.placeholder('upscaling', 'name')
              : isTurntable
                ? 'Auto from output target ID'
                : isEmpty
                  ? d.placeholder('empty', 'name')
                  : d.placeholder('blenderRender', 'name')
          }
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </div>

      {/* Blender render parameters */}
      {isBlender && (
        <>
          {/* Engine */}
          <div className="space-y-1.5">
            <Label>{d.label('blenderRender', 'engine')}</Label>
            <Select
              items={{ cycles: d.engine('cycles'), eevee: d.engine('eevee') }}
              value={form.engine}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, engine: value ?? f.engine }))
              }
            >
              <SelectTrigger
                className="w-full"
                data-testid={TESTID.engineSelect}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cycles">{d.engine('cycles')}</SelectItem>
                <SelectItem value="eevee">{d.engine('eevee')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Resolution */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'width')}</Label>
              <Input
                type="number"
                value={form.width}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v)) setForm((f) => ({ ...f, width: v }))
                }}
              />
              {errors.width !== undefined && (
                <p className={ERROR_CLASS}>{errors.width}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'height')}</Label>
              <Input
                type="number"
                value={form.height}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v)) setForm((f) => ({ ...f, height: v }))
                }}
              />
              {errors.height !== undefined && (
                <p className={ERROR_CLASS}>{errors.height}</p>
              )}
            </div>
          </div>

          {/* Frame range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'frameStart')}</Label>
              <Input
                type="number"
                value={form.frameStart}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v))
                    setForm((f) => ({ ...f, frameStart: v }))
                }}
              />
              {errors.frame_start !== undefined && (
                <p className={ERROR_CLASS}>{errors.frame_start}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'frameEnd')}</Label>
              <Input
                type="number"
                value={form.frameEnd}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v)) setForm((f) => ({ ...f, frameEnd: v }))
                }}
              />
              {errors.frame_end !== undefined && (
                <p className={ERROR_CLASS}>{errors.frame_end}</p>
              )}
            </div>
          </div>

          {/* Output format + samples */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'format')}</Label>
              <Select
                items={{
                  png: d.outputFormat('png'),
                  exr: d.outputFormat('exr'),
                  jpeg: d.outputFormat('jpeg'),
                }}
                value={form.outputFormat}
                onValueChange={(value) =>
                  setForm((f) => ({
                    ...f,
                    outputFormat: value ?? f.outputFormat,
                  }))
                }
              >
                <SelectTrigger
                  className="w-full"
                  data-testid={TESTID.formatSelect}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="png">{d.outputFormat('png')}</SelectItem>
                  <SelectItem value="exr">{d.outputFormat('exr')}</SelectItem>
                  <SelectItem value="jpeg">{d.outputFormat('jpeg')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{d.label('blenderRender', 'samples')}</Label>
              <Input
                type="number"
                value={form.samples}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v)) setForm((f) => ({ ...f, samples: v }))
                }}
              />
              {errors.samples !== undefined && (
                <p className={ERROR_CLASS}>{errors.samples}</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Billing stub */}
      <div className="flex items-center justify-between py-3 px-4 bg-muted rounded-lg">
        <span className="text-sm text-muted-foreground">
          {d.label('job', 'estimatedCost')}
        </span>
        <span className="text-sm font-medium text-muted-foreground">{'—'}</span>
      </div>

      {/* Submit-time server error (validation / rejected job_spec) */}
      {submitError !== null && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          data-testid={TESTID.submitError}
        >
          {submitError}
        </div>
      )}

      {/* Submit */}
      <Button
        className="w-full"
        size="lg"
        data-testid={TESTID.submitButton}
        disabled={isSubmitting || isUploading}
        onClick={onSubmit}
      >
        {isSubmitting || isUploading ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Send />
        )}
        {submitLabel}
      </Button>
    </div>
  )
}
